import { useEffect, useState } from "react";

import {
  Flex,
  Button,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";
import * as Yup from "yup";

import { useQueryParam } from "~/hooks";
import useNamespace from "~/hooks/useNamespace";
import { sendEvent, EventType, FeatureType } from "~/services/amplitude";
import { Password } from "~/theme/components";
import { yupTest } from "~/utils/passVerify";

type CreatePasswordProps = {
  email: string;
  password: string;
  confirm_password: string;
};

type Props = {
  loading: boolean;
  createPassword: (email: string, password: string) => void;
  changeEmail: (email: string) => void;
};

const schema = Yup.object({
  email: Yup.string().email().required("forgotPassword@validation-email-required"),
  password: Yup.string()
    .required("forgotPassword@validation-password-required")
    .test(...yupTest("forgotPassword")),
  confirm_password: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "forgotPassword@validation-confirm-password-invalid"
  ),
}).required();

function CreatePassword({ createPassword, loading, changeEmail }: Props) {
  const { translate } = useNamespace("forgotPassword");
  const emailParam = useQueryParam("email");

  const { formState, register, handleSubmit, getValues } = useForm<CreatePasswordProps>({
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
    criteriaMode: "all",
    mode: "all",
  });

  const onSubmit: SubmitHandler<CreatePasswordProps> = async ({ email, password }) => {
    createPassword(email, password);
  };

  const [inputEmailEnable, setInputEmailEnable] = useState(emailParam.email ? true : false);

  const enableEmailField = () => {
    setInputEmailEnable(false);
  };

  useEffect(() => {
    if (Object.keys(formState.errors).length) {
      sendEvent({ event: EventType.INPUT_ERROR_OCCURRED, feature: FeatureType.ACTIVATION });
    }
  }, [formState.errors]);

  const onChangeEmail = () => {
    const emailValue = emailParam.email || getValues("email");

    if (emailValue) changeEmail(emailValue);
  };

  return (
    <div className="w-full bg-basic-white-50 p-[38px] rounded-3xl">
      <FormControl display="flex" flexDirection="column" width="100%" gridRowGap={["4", "8"]} as="form">
        <Flex direction="column" width="100%" alignItems="flex-start" gridRowGap={["2", "4"]}>
          <Flex direction="column" width="inherit">
            <FormLabel htmlFor="email" textStyle="label.primary" color="black">
              {translate("forgotPassword@form-email-label")}
            </FormLabel>
            <InputGroup>
              <Input
                id="email"
                type="email"
                disabled={inputEmailEnable}
                data-testid="email"
                isInvalid={!!formState.errors.email}
                {...register("email", { required: true, value: emailParam.email || "", onBlur: onChangeEmail })}
                required
                autoComplete="email"
              />
              {emailParam.email && (
                <InputRightElement>
                  <Button
                    color={"purple.300"}
                    textDecoration="underline"
                    marginRight="5"
                    size="sm"
                    variant="unstyled"
                    onClick={enableEmailField}
                    minWidth="auto"
                  >
                    {translate("forgotPassword@change")}
                  </Button>
                </InputRightElement>
              )}
            </InputGroup>
          </Flex>
          <Flex direction="column" width="inherit">
            <FormLabel htmlFor="password" textStyle="label.primary" color="black">
              {translate("forgotPassword@form-password-label")}
            </FormLabel>
            <Password
              id="password"
              data-testid="password"
              isInvalid={!!formState.errors.password}
              {...register("password")}
              required
              autoComplete="new-password"
              hint
            />
          </Flex>
          <Flex direction="column" width="inherit">
            <FormLabel htmlFor="confirm_password" textStyle="label.primary" color="black">
              {translate("forgotPassword@form-confirm-password-label")}
            </FormLabel>
            <Password
              id="confirm_password"
              data-testid="confirm_password"
              isInvalid={!!formState.errors.confirm_password}
              {...register("confirm_password")}
              required
              autoComplete="new-password"
            />
            {formState.errors.confirm_password && (
              <FormErrorMessage>
                {translate(formState.errors.confirm_password.message as "forgotPassword@validation-password-required")}
              </FormErrorMessage>
            )}
          </Flex>
        </Flex>
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          data-testid="submit"
          data-type="primary"
          isDisabled={!formState.isValid}
          isLoading={loading}
          borderRadius={30}
          isFullWidth
        >
          {translate("forgotPassword@create-password-signin")}
        </Button>
      </FormControl>
    </div>
  );
}

export default CreatePassword;
